import { supabase } from '../lib/supabase';
import {
  buildMetricCard,
  buildTrendSeries,
  countByField,
  countByMonth,
  getMonthBuckets,
  getTrendStartDate,
} from '../utils/dashboardAnalytics';

const PENDING_REG_STATUSES = ['submitted', 'under_review'];

const periodRange = (daysAgoStart, daysAgoEnd) => {
  const end = new Date();
  end.setDate(end.getDate() - daysAgoStart);
  const start = new Date();
  start.setDate(start.getDate() - daysAgoEnd);
  return { start: start.toISOString(), end: end.toISOString() };
};

const countInRange = async (table, start, end, filters = {}) => {
  let query = supabase
    .from(table)
    .select('*', { count: 'exact', head: true })
    .gte('created_at', start)
    .lt('created_at', end);

  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) query = query.in(key, value);
    else query = query.eq(key, value);
  });

  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
};

const fetchCreatedSince = async (table, columns = 'created_at') => {
  const { data, error } = await supabase
    .from(table)
    .select(columns)
    .gte('created_at', getTrendStartDate().toISOString());
  if (error) throw error;
  return data || [];
};

const fetchStatusRows = async (table, statusColumn) => {
  const { data, error } = await supabase.from(table).select(statusColumn);
  if (error) throw error;
  return data || [];
};

const resolveTeamNames = async (teamIds) => {
  const uniqueIds = [...new Set(teamIds.filter(Boolean))];
  if (!uniqueIds.length) return {};
  const { data } = await supabase.from('teams').select('id, name').in('id', uniqueIds);
  const map = {};
  (data || []).forEach((team) => {
    map[team.id] = team.name;
  });
  return map;
};

const resolveRegionalTeamNames = async (teamIds) => {
  const uniqueIds = [...new Set(teamIds.filter(Boolean))];
  if (!uniqueIds.length) return {};
  const { data } = await supabase.from('regional_teams').select('id, team_name').in('id', uniqueIds);
  const map = {};
  (data || []).forEach((team) => {
    map[team.id] = team.team_name;
  });
  return map;
};

const resolveLeagueNames = async (leagueIds) => {
  const uniqueIds = [...new Set(leagueIds.filter(Boolean))];
  if (!uniqueIds.length) return {};
  const { data } = await supabase.from('leagues').select('id, league_name').in('id', uniqueIds);
  const map = {};
  (data || []).forEach((league) => {
    map[league.id] = league.league_name;
  });
  return map;
};

/**
 * Dashboard Service
 * Fetches real-time data for the admin dashboard
 */

export const dashboardService = {
  /**
   * Get dashboard metrics
   */
  async getMetrics() {
    try {
      // Get total teams
      const { count: totalTeams } = await supabase
        .from('teams')
        .select('*', { count: 'exact', head: true });

      // Get active teams (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const { count: recentTeams } = await supabase
        .from('teams')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      // Get total players
      const { count: totalPlayers } = await supabase
        .from('players')
        .select('*', { count: 'exact', head: true });

      // Get recent players (last 30 days)
      const { count: recentPlayers } = await supabase
        .from('players')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      // Get active officials (users with official role)
      const { count: totalOfficials } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'official')
        .eq('is_active', true);

      // Get recent officials (last 30 days)
      const { count: recentOfficials } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'official')
        .eq('is_active', true)
        .gte('created_at', thirtyDaysAgo.toISOString());

      // Get active leagues
      const { count: activeLeagues } = await supabase
        .from('leagues')
        .select('*', { count: 'exact', head: true })
        .in('league_status', ['upcoming', 'active']);

      return {
        totalTeams: totalTeams || 0,
        recentTeams: recentTeams || 0,
        totalPlayers: totalPlayers || 0,
        recentPlayers: recentPlayers || 0,
        totalOfficials: totalOfficials || 0,
        recentOfficials: recentOfficials || 0,
        activeLeagues: activeLeagues || 0,
      };
    } catch (error) {
      console.error('Error fetching metrics:', error);
      throw new Error('Failed to fetch dashboard metrics');
    }
  },

  /**
   * Get recent activities
   */
  async getRecentActivities(limit = 20) {
    try {
      const activities = [];

      // Get recent team registrations
      const { data: recentTeams } = await supabase
        .from('teams')
        .select(`
          id,
          name,
          created_at,
          team_manager_id
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      // Get team manager names separately
      const managerIds = recentTeams?.map(t => t.team_manager_id).filter(Boolean) || [];
      const managerNames = {};
      if (managerIds.length > 0) {
        const { data: managers } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .in('id', managerIds);
        
        managers?.forEach(m => {
          managerNames[m.id] = m.full_name;
        });
      }

      if (recentTeams) {
        recentTeams.forEach((team) => {
          activities.push({
            id: `team_${team.id}`,
            type: 'team_registration',
            description: `New team '${team.name}' registered`,
            user: managerNames[team.team_manager_id] || 'System',
            timestamp: new Date(team.created_at),
            entityId: team.id,
            entityType: 'team',
          });
        });
      }

      // Get recent player registrations
      const { data: recentPlayers } = await supabase
        .from('players')
        .select(`
          id,
          created_at,
          user_profile_id,
          team_id
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      // Get player and team names separately
      const playerUserIds = recentPlayers?.map(p => p.user_profile_id).filter(Boolean) || [];
      const teamIds = recentPlayers?.map(p => p.team_id).filter(Boolean) || [];
      
      const playerNames = {};
      if (playerUserIds.length > 0) {
        const { data: users } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .in('id', playerUserIds);
        
        users?.forEach(u => {
          playerNames[u.id] = u.full_name;
        });
      }

      const teamNames = {};
      if (teamIds.length > 0) {
        const { data: teams } = await supabase
          .from('teams')
          .select('id, name')
          .in('id', teamIds);
        
        teams?.forEach(t => {
          teamNames[t.id] = t.name;
        });
      }

      if (recentPlayers) {
        recentPlayers.forEach((player) => {
          const playerName = playerNames[player.user_profile_id] || 'Unknown';
          const teamName = player.team_id ? teamNames[player.team_id] : null;
          activities.push({
            id: `player_${player.id}`,
            type: 'player_registration',
            description: `Player ${playerName} registered${teamName ? ` for ${teamName}` : ''}`,
            user: playerName,
            timestamp: new Date(player.created_at),
            entityId: player.id,
            entityType: 'player',
          });
        });
      }

      // Get recent match results
      const { data: recentMatches } = await supabase
        .from('matches')
        .select(`
          id,
          home_score,
          away_score,
          ended_at,
          home_team_id,
          away_team_id
        `)
        .eq('match_status', 'completed')
        .not('ended_at', 'is', null)
        .order('ended_at', { ascending: false })
        .limit(limit);

      // Get match team names
      const matchTeamIds = [];
      recentMatches?.forEach(m => {
        if (m.home_team_id) matchTeamIds.push(m.home_team_id);
        if (m.away_team_id) matchTeamIds.push(m.away_team_id);
      });
      
      const matchTeamNames = {};
      if (matchTeamIds.length > 0) {
        const { data: matchTeams } = await supabase
          .from('teams')
          .select('id, name')
          .in('id', [...new Set(matchTeamIds)]);
        
        matchTeams?.forEach(t => {
          matchTeamNames[t.id] = t.name;
        });
      }

      if (recentMatches) {
        recentMatches.forEach((match) => {
          const homeTeam = matchTeamNames[match.home_team_id] || 'Unknown';
          const awayTeam = matchTeamNames[match.away_team_id] || 'Unknown';
          activities.push({
            id: `match_${match.id}`,
            type: 'match_result',
            description: `Match result: ${homeTeam} ${match.home_score}-${match.away_score} ${awayTeam}`,
            user: 'System',
            timestamp: new Date(match.ended_at),
            entityId: match.id,
            entityType: 'match',
          });
        });
      }

      // Get recent user registrations
      const { data: recentUsers } = await supabase
        .from('user_profiles')
        .select('id, full_name, role, created_at')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (recentUsers) {
        recentUsers.forEach((user) => {
          const roleDisplay = user.role?.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || 'User';
          activities.push({
            id: `user_${user.id}`,
            type: 'user_registration',
            description: `New ${roleDisplay} account created: ${user.full_name}`,
            user: 'System',
            timestamp: new Date(user.created_at),
            entityId: user.id,
            entityType: 'user',
          });
        });
      }

      // Get recent team approvals
      const { data: approvedTeams } = await supabase
        .from('teams')
        .select(`
          id,
          name,
          approved_date,
          approved_by
        `)
        .not('approved_date', 'is', null)
        .order('approved_date', { ascending: false })
        .limit(limit);

      // Get approver names
      const approverIds = approvedTeams?.map(t => t.approved_by).filter(Boolean) || [];
      const approverNames = {};
      if (approverIds.length > 0) {
        const { data: approvers } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .in('id', approverIds);
        
        approvers?.forEach(a => {
          approverNames[a.id] = a.full_name;
        });
      }

      if (approvedTeams) {
        approvedTeams.forEach((team) => {
          activities.push({
            id: `approval_${team.id}`,
            type: 'team_approval',
            description: `Team '${team.name}' approved`,
            user: approverNames[team.approved_by] || 'Admin',
            timestamp: new Date(team.approved_date),
            entityId: team.id,
            entityType: 'team',
          });
        });
      }

      // Sort all activities by timestamp (most recent first) and limit
      activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      return activities.slice(0, limit);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      throw new Error('Failed to fetch recent activities');
    }
  },

  /**
   * Get pending actions counts
   */
  async getPendingActions() {
    try {
      // Teams pending approval
      const { count: pendingTeams } = await supabase
        .from('teams')
        .select('*', { count: 'exact', head: true })
        .eq('registration_status', 'submitted')
        .or('team_status.eq.pending_approval,registration_status.eq.under_review');

      // Players pending approval
      const { count: pendingPlayers } = await supabase
        .from('players')
        .select('*', { count: 'exact', head: true })
        .in('registration_status', ['submitted', 'under_review']);

      // Matches needing officials (matches with at least one missing official)
      const { data: scheduledMatches } = await supabase
        .from('matches')
        .select('id, referee_id, umpire1_id, umpire2_id')
        .eq('match_status', 'scheduled');
      
      const matchesNeedingOfficials = scheduledMatches?.filter(match => 
        !match.referee_id || !match.umpire1_id || !match.umpire2_id
      ).length || 0;

      // Leagues needing updates (simplified - just count active leagues)
      const { count: leaguesNeedingUpdates } = await supabase
        .from('leagues')
        .select('*', { count: 'exact', head: true })
        .in('league_status', ['upcoming', 'active']);

      return {
        teamApprovals: pendingTeams || 0,
        playerTransfers: pendingPlayers || 0,
        officialAssignments: matchesNeedingOfficials || 0,
        leagueUpdates: leaguesNeedingUpdates || 0,
      };
    } catch (error) {
      console.error('Error fetching pending actions:', error);
      throw new Error('Failed to fetch pending actions');
    }
  },

  /**
   * Comprehensive analytics for admin dashboard
   */
  async getAnalytics() {
    try {
      const buckets = getMonthBuckets();
      const last30 = periodRange(0, 30);
      const prev30 = periodRange(30, 60);

      const [
        teamsTrend,
        playersTrend,
        coachesTrend,
        regionalTeamsTrend,
        teamStatuses,
        playerStatuses,
        coachStatuses,
        leagueStatuses,
        tournamentStatuses,
        matchStatuses,
        tournamentGameStatuses,
        coachesRegional,
        teamsRegional,
        upcomingMatches,
        recentMatches,
        upcomingTournamentGames,
        recentTournamentGames,
        pendingRegions,
        pendingPlayers,
        pendingTeams,
        pendingRegionalTeams,
        pendingCoaches,
        matchesNeedingOfficials,
        totalTeams,
        totalPlayers,
        totalCoaches,
        totalRegionalTeams,
        totalOfficials,
        totalRegionAssociations,
        totalLeagues,
        totalTournaments,
        teamsLast30,
        teamsPrev30,
        playersLast30,
        playersPrev30,
        coachesLast30,
        coachesPrev30,
        regionalTeamsLast30,
        regionalTeamsPrev30,
        activities,
      ] = await Promise.all([
        fetchCreatedSince('teams'),
        fetchCreatedSince('players'),
        fetchCreatedSince('coaches'),
        fetchCreatedSince('regional_teams'),
        fetchStatusRows('teams', 'registration_status, team_status'),
        fetchStatusRows('players', 'registration_status, player_status'),
        fetchStatusRows('coaches', 'registration_status, license_status'),
        fetchStatusRows('leagues', 'league_status'),
        fetchStatusRows('tournaments', 'tournament_status'),
        fetchStatusRows('matches', 'match_status, ended_at, created_at'),
        fetchStatusRows('tournament_games', 'game_status, updated_at, created_at'),
        supabase.from('coaches').select('region').then(({ data, error }) => {
          if (error) throw error;
          return data || [];
        }),
        supabase.from('teams').select('region').then(({ data, error }) => {
          if (error) throw error;
          return data || [];
        }),
        supabase
          .from('matches')
          .select('id, scheduled_date, venue, home_team_id, away_team_id, league_id, match_status')
          .eq('match_status', 'scheduled')
          .gte('scheduled_date', new Date().toISOString())
          .order('scheduled_date', { ascending: true })
          .limit(5),
        supabase
          .from('matches')
          .select('id, home_score, away_score, ended_at, home_team_id, away_team_id, league_id, match_status')
          .eq('match_status', 'completed')
          .not('ended_at', 'is', null)
          .order('ended_at', { ascending: false })
          .limit(5),
        supabase
          .from('tournament_games')
          .select('id, scheduled_date, venue, home_team_id, away_team_id, game_status')
          .eq('game_status', 'scheduled')
          .gte('scheduled_date', new Date().toISOString())
          .order('scheduled_date', { ascending: true })
          .limit(5),
        supabase
          .from('tournament_games')
          .select('id, home_score, away_score, updated_at, home_team_id, away_team_id, game_status')
          .eq('game_status', 'completed')
          .order('updated_at', { ascending: false })
          .limit(5),
        supabase.from('region_associations').select('*', { count: 'exact', head: true }).eq('registration_status', 'pending'),
        supabase.from('players').select('*', { count: 'exact', head: true }).in('registration_status', PENDING_REG_STATUSES),
        supabase
          .from('teams')
          .select('*', { count: 'exact', head: true })
          .or(`team_status.eq.pending_approval,registration_status.in.(${PENDING_REG_STATUSES.join(',')})`),
        supabase.from('regional_teams').select('*', { count: 'exact', head: true }).eq('registration_status', 'pending'),
        supabase.from('coaches').select('*', { count: 'exact', head: true }).in('registration_status', PENDING_REG_STATUSES),
        supabase.from('matches').select('id, referee_id, umpire1_id, umpire2_id').eq('match_status', 'scheduled'),
        supabase.from('teams').select('*', { count: 'exact', head: true }),
        supabase.from('players').select('*', { count: 'exact', head: true }),
        supabase.from('coaches').select('*', { count: 'exact', head: true }),
        supabase.from('regional_teams').select('*', { count: 'exact', head: true }),
        supabase.from('user_profiles').select('*', { count: 'exact', head: true }).eq('role', 'official').eq('is_active', true),
        supabase.from('region_associations').select('*', { count: 'exact', head: true }),
        supabase.from('leagues').select('*', { count: 'exact', head: true }),
        supabase.from('tournaments').select('*', { count: 'exact', head: true }),
        countInRange('teams', last30.start, last30.end),
        countInRange('teams', prev30.start, prev30.end),
        countInRange('players', last30.start, last30.end),
        countInRange('players', prev30.start, prev30.end),
        countInRange('coaches', last30.start, last30.end),
        countInRange('coaches', prev30.start, prev30.end),
        countInRange('regional_teams', last30.start, last30.end),
        countInRange('regional_teams', prev30.start, prev30.end),
        this.getRecentActivities(12),
      ]);

      const teamMonthCounts = countByMonth(teamsTrend, 'created_at', buckets);
      const playerMonthCounts = countByMonth(playersTrend, 'created_at', buckets);
      const coachMonthCounts = countByMonth(coachesTrend, 'created_at', buckets);
      const regionalMonthCounts = countByMonth(regionalTeamsTrend, 'created_at', buckets);

      const registrationTrend = buildTrendSeries(buckets, [
        { key: 'teams', counts: teamMonthCounts },
        { key: 'players', counts: playerMonthCounts },
        { key: 'coaches', counts: coachMonthCounts },
        { key: 'regionalTeams', counts: regionalMonthCounts },
      ]);

      const leagueMatchMonthCounts = countByMonth(
        matchStatuses.filter((m) => m.match_status === 'completed'),
        'ended_at',
        buckets
      );
      const tournamentMatchMonthCounts = countByMonth(
        tournamentGameStatuses.filter((g) => g.game_status === 'completed'),
        'updated_at',
        buckets
      );

      const matchCompletionTrend = buildTrendSeries(buckets, [
        { key: 'leagueMatches', counts: leagueMatchMonthCounts },
        { key: 'tournamentGames', counts: tournamentMatchMonthCounts },
      ]);

      const activeLeagues = leagueStatuses.filter((l) =>
        ['upcoming', 'active'].includes(l.league_status)
      ).length;
      const activeTournaments = tournamentStatuses.filter((t) =>
        ['upcoming', 'active'].includes(t.tournament_status)
      ).length;

      const leagueMatchCounts = countByField(matchStatuses, 'match_status');
      const tournamentGameCounts = countByField(tournamentGameStatuses, 'game_status');

      const regionalMap = {};
      [...coachesRegional, ...teamsRegional].forEach((row) => {
        const region = row.region || 'Unassigned';
        regionalMap[region] = (regionalMap[region] || 0) + 1;
      });
      const regionalDistribution = Object.entries(regionalMap)
        .map(([region, count]) => ({ region, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const matchTeamIds = [];
      (upcomingMatches.data || []).forEach((m) => {
        matchTeamIds.push(m.home_team_id, m.away_team_id);
      });
      (recentMatches.data || []).forEach((m) => {
        matchTeamIds.push(m.home_team_id, m.away_team_id);
      });
      const leagueIds = (recentMatches.data || []).map((m) => m.league_id);

      const regionalIds = [];
      (upcomingTournamentGames.data || []).forEach((g) => {
        regionalIds.push(g.home_team_id, g.away_team_id);
      });
      (recentTournamentGames.data || []).forEach((g) => {
        regionalIds.push(g.home_team_id, g.away_team_id);
      });

      const [teamNames, leagueNames, regionalNames] = await Promise.all([
        resolveTeamNames(matchTeamIds),
        resolveLeagueNames(leagueIds),
        resolveRegionalTeamNames(regionalIds),
      ]);

      const upcomingFixtures = [
        ...(upcomingMatches.data || []).map((match) => ({
          id: match.id,
          type: 'league',
          homeTeam: teamNames[match.home_team_id] || 'Home Team',
          awayTeam: teamNames[match.away_team_id] || 'Away Team',
          date: match.scheduled_date,
          venue: match.venue || 'TBD',
          location: 'League Match',
        })),
        ...(upcomingTournamentGames.data || []).map((game) => ({
          id: game.id,
          type: 'tournament',
          homeTeam: regionalNames[game.home_team_id] || 'Home Team',
          awayTeam: regionalNames[game.away_team_id] || 'Away Team',
          date: game.scheduled_date,
          venue: game.venue || 'TBD',
          location: 'Tournament Game',
        })),
      ]
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5);

      const recentResults = [
        ...(recentMatches.data || []).map((match) => ({
          id: match.id,
          type: 'league',
          homeTeam: teamNames[match.home_team_id] || 'Home Team',
          awayTeam: teamNames[match.away_team_id] || 'Away Team',
          homeScore: match.home_score,
          awayScore: match.away_score,
          date: match.ended_at,
          league: leagueNames[match.league_id] || 'League',
        })),
        ...(recentTournamentGames.data || []).map((game) => ({
          id: game.id,
          type: 'tournament',
          homeTeam: regionalNames[game.home_team_id] || 'Home Team',
          awayTeam: regionalNames[game.away_team_id] || 'Away Team',
          homeScore: game.home_score,
          awayScore: game.away_score,
          date: game.updated_at,
          league: 'Tournament',
        })),
      ]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 5);

      const officialsGap =
        (matchesNeedingOfficials.data || []).filter(
          (match) => !match.referee_id || !match.umpire1_id || !match.umpire2_id
        ).length || 0;

      const pendingApprovals = {
        regions: pendingRegions.count || 0,
        players: pendingPlayers.count || 0,
        teams: pendingTeams.count || 0,
        regionalTeams: pendingRegionalTeams.count || 0,
        coaches: pendingCoaches.count || 0,
        total:
          (pendingRegions.count || 0) +
          (pendingPlayers.count || 0) +
          (pendingTeams.count || 0) +
          (pendingRegionalTeams.count || 0) +
          (pendingCoaches.count || 0),
      };

      return {
        summary: {
          teams: buildMetricCard({
            total: totalTeams.count || 0,
            currentPeriod: teamsLast30,
            previousPeriod: teamsPrev30,
          }),
          players: buildMetricCard({
            total: totalPlayers.count || 0,
            currentPeriod: playersLast30,
            previousPeriod: playersPrev30,
          }),
          coaches: buildMetricCard({
            total: totalCoaches.count || 0,
            currentPeriod: coachesLast30,
            previousPeriod: coachesPrev30,
          }),
          regionalTeams: buildMetricCard({
            total: totalRegionalTeams.count || 0,
            currentPeriod: regionalTeamsLast30,
            previousPeriod: regionalTeamsPrev30,
          }),
          officials: {
            value: totalOfficials.count || 0,
            change: `${officialsGap} unassigned match${officialsGap === 1 ? '' : 'es'}`,
            changeType: officialsGap > 0 ? 'negative' : 'positive',
            trend: 'Scheduled league fixtures',
          },
          regionAssociations: {
            value: totalRegionAssociations.count || 0,
            change: `${pendingApprovals.regions} pending approval`,
            changeType: pendingApprovals.regions > 0 ? 'negative' : 'neutral',
            trend: 'Region association registrations',
          },
          leagues: {
            value: totalLeagues.count || 0,
            change: `${activeLeagues} currently active`,
            changeType: 'neutral',
            trend: 'Upcoming and in-progress leagues',
          },
          tournaments: {
            value: totalTournaments.count || 0,
            change: `${activeTournaments} currently active`,
            changeType: 'neutral',
            trend: 'Upcoming and in-progress tournaments',
          },
          leagueMatches: {
            value: matchStatuses.length,
            change: `${matchStatuses.filter((m) => m.match_status === 'completed').length} completed`,
            changeType: 'neutral',
            trend: 'Total league fixtures recorded',
          },
          tournamentGames: {
            value: tournamentGameStatuses.length,
            change: `${tournamentGameStatuses.filter((g) => g.game_status === 'completed').length} completed`,
            changeType: 'neutral',
            trend: 'Total tournament games recorded',
          },
        },
        registrationTrend,
        matchCompletionTrend,
        registrationStatus: {
          teams: countByField(teamStatuses, 'registration_status'),
          players: countByField(playerStatuses, 'registration_status'),
          coaches: countByField(coachStatuses, 'registration_status'),
        },
        licenseStatus: countByField(coachStatuses, 'license_status'),
        eventStatus: {
          leagues: countByField(leagueStatuses, 'league_status'),
          tournaments: countByField(tournamentStatuses, 'tournament_status'),
        },
        matchStatus: {
          league: leagueMatchCounts,
          tournament: tournamentGameCounts,
        },
        regionalDistribution,
        pendingApprovals,
        pendingActions: {
          teamApprovals: pendingApprovals.teams,
          playerTransfers: pendingApprovals.players,
          officialAssignments: officialsGap,
          leagueUpdates: activeLeagues,
          coachApprovals: pendingApprovals.coaches,
          regionalTeamApprovals: pendingApprovals.regionalTeams,
        },
        upcomingFixtures,
        recentResults,
        recentActivities: activities,
      };
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
      throw new Error(error.message || 'Failed to fetch dashboard analytics');
    }
  },
};

